<template>
  <ProductListLayout
    v-if="isLoadingWithMinTime || store.softwareDownloads?.results.length"
    :results-text="softwareCountInformation"
    :is-loading="isLoadingWithMinTime"
    :show-filter="store.softwareDownloads?.filters.length !== 0"
    :content-heading-id="
      $textDictionary['softwareCenter.list.contentHeadingId']
    "
    :content-heading="$textDictionary['softwareCenter.list.contentHeading']"
    :filters-skip-btn-text="
      $textDictionary['softwareCenter.filter.filtersSkipBtnText.label']
    "
    :filters-open-btn-text="
      $textDictionary['softwareCenter.filter.filtersOpenBtnText.label']
    "
    :filters-modal-title="
      $textDictionary['softwareCenter.filterModal.filtersModalTitle.label']
    "
    :filters-modal-close-icon-text="
      $textDictionary[
        'softwareCenter.filterModal.filtersModalCloseIconText.label'
      ]
    "
    :filters-modal-apply-btn-text="
      $textDictionary[
        'softwareCenter.filterModal.filtersModalApplyBtnText.label'
      ]
    "
    :data-insights-index="store.indexName"
    :data-insights-query-id="store.queryId"
  >
    <template
      v-if="
        store.softwareDownloads && store.softwareDownloads?.filters.length !== 0
      "
      #filter
    >
      <SoftwareCenterFilters />
    </template>
    <template v-if="store.filters" #filterTags>
      <HeadlineModule
        v-if="store.isInitialView"
        :level="4"
        :content="
          $textDictionary['softwareCenter.initialView.headline'] ??
          'Latest Releases'
        "
      />
      <TagList
        v-else
        :tags="formattedFilterFacets"
        :is-small="true"
        @click="handleTagClick"
      />
    </template>
    <template #list>
      <DownloadCards
        size="three-quarters"
        :items="formattedData"
        :is-loading="isLoadingWithMinTime"
        @open="openModal"
      />
      <Button
        v-if="store.canLoadMore"
        style-type="link"
        icon-type="icon-left"
        icon-name="arrow-down"
        @click="store.fetchMoreSoftwareDownloads"
      >
        {{
          $textDictionary['softwareCenter.showMoreButton.label'] ?? 'Show more'
        }}
      </Button>
    </template>
  </ProductListLayout>
  <div
    v-if="!isLoadingWithMinTime && !store.softwareDownloads?.results.length"
    class="search-page-layout__empty-content"
  >
    <div class="search-page-layout__empty-content-text rte-text">
      <LibRichTextRenderer :rich-text="noResultsRichText" />
    </div>
    <tag-list-group
      v-bind="{
        headlineLevel: 4,
        items: [tagListGroupArgs.items[2]]
      }"
    />
  </div>
  <SoftwareCenterModalContainer
    :abort-function="onAbortModal"
    :after-close-function="onAfterCloseModal"
  />
</template>

<script setup lang="ts">
import { useDownloadStore } from '~/stores/downloadStore/softwareDownloadStore';
import {
  ProductListLayout,
  TagList,
  DownloadCards,
  HeadlineModule,
  Button,
  TagListGroup
} from '@hypercodestudio/basler-components';

import type { Item } from '@hypercodestudio/basler-components/dist/components/modules/DownloadCards.vue';
import type { TagInterface } from '@hypercodestudio/basler-components/dist/components/elements/taglist/TagList.vue';
import type {
  SoftwareEntryModel,
  SoftwareGroupedAssetModel
} from '~/lib/DownloadService/model';
import { useSoftwareOsLabel } from '~/lib/DownloadService/helper/useSoftwareOsLabel';
import { generateImageObject } from '~/utils/shop/generateImageObject';
import SoftwareCenterFilters from '~/components/downloads/software/SoftwareCenterFilters.vue';
import SoftwareCenterModalContainer from '~/components/downloads/software/SoftwareCenterModalContainer.vue';
import { useSearchSuggestions } from '~/composables/useSearchSuggestions';

const route = useRoute();
const store = useDownloadStore();
const locale = useLocale();

const { $textDictionary, $dictionary, $globalPageSettings } = useNuxtApp();
const htmlDictionary = useHTMLDictionary(['softwareCenter.totalCount.label']);

const { tagListGroupArgs } = useSearchSuggestions();

// Minimum loading time for skeletons (1.5 seconds)
const MINIMUM_LOADING_TIME = 1500;
const isLoadingWithMinTime = ref(false);
const loadingStartTime = ref<number | null>(null);

/**
 * Start loading with minimum time tracking
 */
function startLoadingWithMinTime() {
  isLoadingWithMinTime.value = true;
  loadingStartTime.value = Date.now();
}

/**
 * Stop loading with minimum time enforcement
 */
async function stopLoadingWithMinTime() {
  if (loadingStartTime.value) {
    const elapsedTime = Date.now() - loadingStartTime.value;
    const remainingTime = MINIMUM_LOADING_TIME - elapsedTime;

    if (remainingTime > 0) {
      // Wait for the remaining time to reach minimum loading time
      await new Promise((resolve) => setTimeout(resolve, remainingTime));
    }
  }

  isLoadingWithMinTime.value = false;
  loadingStartTime.value = null;
}

/**
 * Wrapper for store operations with minimum loading time
 */
async function executeOperationWithMinTime(operation: () => Promise<void>) {
  // Start timing
  startLoadingWithMinTime();

  try {
    // Execute the operation
    await operation();
  } finally {
    // Enforce minimum loading time
    await stopLoadingWithMinTime();
  }
}

const onAbortModal = () => {
  store.activeModal = false;
};

const onAfterCloseModal = () => {
  store.activeModal = false;
};

/**
 * Handle tag click event from TagList to remove filters
 */
function handleTagClick(tag: any): void {
  if (!tag || !tag.id) {
    return;
  }

  // Parse the tag ID to get filter name and value
  // Tag ID format: "filterName_filterValue"
  const parts = tag.id.split('_');
  if (parts.length < 2) {
    return;
  }

  // Reconstruct the filter name and value
  // Handle cases where the filter name itself contains underscores
  const filterName = parts.slice(0, -1).join('_');
  const filterValue = parts[parts.length - 1];

  // Get current filter values
  const currentValues = store.filters[filterName] || [];

  // Remove the specific value from the filter
  const newValues = currentValues.filter(
    (value: string) => value !== filterValue
  );

  // Update the filter with the remaining values
  // This will trigger the route update and search automatically
  store.handleFormValueChange(filterName, newValues);
}

// Trigger loading when component is mounted with a search query and software tab is active
onMounted(() => {
  if (route.query.q && route.query.type === 'software') {
    // Check if we already have results for this search query
    const hasExistingResults =
      store.softwareDownloads?.query === route.query.q?.toString()?.trim() &&
      store.softwareDownloads?.maxCount !== undefined &&
      !store.loading;

    if (hasExistingResults) {
      // We already have results, just update without minimum loading time
      store.updateFromRouteAndFetch(route.query);
    } else {
      // No existing results, apply minimum loading time
      executeOperationWithMinTime(async () => {
        await store.updateFromRouteAndFetch(route.query);
      });
    }
  }
});

watch(
  () => route.query,
  async (newQuery, oldQuery) => {
    let queryChanged = false;
    let tabChanged = false;
    let filtersChanged = false;

    // Check if the search query has changed
    if (newQuery.q !== oldQuery?.q) {
      if (newQuery.q) {
        store.query = newQuery.q.toString();
      }
      queryChanged = true;
    }

    // Check if the tab type has changed to software
    if (newQuery.type !== oldQuery?.type && newQuery.type === 'software') {
      tabChanged = true;
    }

    // Check if any filter parameters have changed
    const allowedFacetKeys = [
      'downloadCategory.values.label.data',
      'groupAssets.supportedOs.data',
      'softwareVersion.data'
    ];

    for (const key of allowedFacetKeys) {
      const newValue = newQuery[key];
      const oldValue = oldQuery?.[key];

      // Compare filter values (handle both single values and arrays)
      const newValueArray = Array.isArray(newValue)
        ? newValue
        : newValue
        ? [newValue]
        : [];
      const oldValueArray = Array.isArray(oldValue)
        ? oldValue
        : oldValue
        ? [oldValue]
        : [];

      if (
        JSON.stringify(newValueArray.sort()) !==
        JSON.stringify(oldValueArray.sort())
      ) {
        filtersChanged = true;
        break;
      }
    }

    // If the query parameter changed OR tab switched to software OR filters changed, trigger a search
    if (queryChanged || tabChanged || filtersChanged) {
      // Check if we already have results for this search query and filters
      const hasExistingResults =
        store.softwareDownloads?.query === newQuery.q?.toString()?.trim() &&
        store.softwareDownloads?.maxCount !== undefined &&
        !store.loading &&
        !filtersChanged; // Don't skip loading if filters changed

      if (hasExistingResults) {
        // We already have results, just update the store without minimum loading time
        await store.updateFromRouteAndFetch(newQuery);
      } else {
        // No existing results or filters changed, apply minimum loading time
        await executeOperationWithMinTime(async () => {
          await store.updateFromRouteAndFetch(newQuery);
        });
      }
    }
  },
  { immediate: true }
);

// watch([() => route.params], () => store.handleRouteChange(route.params), {
//   immediate: true
// });

const softwareCenterStartSlug = computed(
  () => $globalPageSettings.value?.softwareCenterPage?.slug
);

const softwareCountInformation = computed(() => {
  const maxCount = store.softwareDownloads?.maxCount ?? 0;
  const count = store.softwareDownloads?.results.length ?? 0;
  const start =
    store.softwareDownloads?.results &&
    store.softwareDownloads?.results.length === 0
      ? 0
      : 1;

  return (
    htmlDictionary.value['softwareCenter.totalCount.label']
      ?.replace('{start}', start.toString() ?? 1)
      ?.replace('{count}', count.toString() ?? 0)
      ?.replace('{total}', maxCount.toString()) ?? ''
  );
});

const openModal = async (downloadId: string) => {
  await store.openModal(downloadId);
};

const noResultsRichText =
  $dictionary.value['softwareCenter.searchResults.noResults'];

const formattedFilterFacets = computed((): TagInterface[] => {
  const validFilters: Record<string, string[]> = {};

  for (const key in store.softwareDownloads?.filters) {
    const k = Number(key);
    const filtername = store.softwareDownloads?.filters[k].name;
    if (k >= 0 && filtername && store.filters[filtername]) {
      validFilters[filtername] = store.filters[filtername];
    }
  }

  const facets: TagInterface[] = [];
  for (const key in validFilters) {
    for (const filter of validFilters[key]) {
      const filterLabelPrefix = 'softwareCenter.softwareFilter.';

      const filterLabel = computed(
        () => $textDictionary.value[filterLabelPrefix + key + '.label'] ?? key
      );
      facets.push({
        text: filterLabel.value + ': ' + useSoftwareOsLabel(filter),
        showCloseIcon: true,
        element: 'button',
        id: `${key}_${filter}`
      });
    }
  }
  return facets;
});

function findPlatforms(
  groupAssets?: ReadonlyArray<SoftwareGroupedAssetModel>
): string[] {
  if (!groupAssets) {
    return [];
  }

  const platforms: string[] = [];
  for (const asset of groupAssets) {
    if (!asset.supportedOs || !Array.isArray(asset.supportedOs)) {
      continue;
    }
    for (const os of asset.supportedOs) {
      if (!platforms.includes(os)) {
        platforms.push(os);
      }
    }
  }

  return platforms;
}

const formattedData = computed<Item[]>(() => {
  const searchParams = new URLSearchParams();
  Object.entries(store.filters).forEach(([key, values]) => {
    values.forEach((value) => searchParams.append(key, value));
  });

  const results = store.softwareDownloads?.results.map<Item>(
    (softwareItem: SoftwareEntryModel, index) => ({
      id: softwareItem.id,
      cardDataAttributes: {
        'insights-object-id': softwareItem.id,
        'insights-position': index + 1
      },
      header: softwareItem.image
        ? {
            type: 'image',
            content: {
              width: 400,
              srcset: softwareItem.image + ' 640w',
              alt: softwareItem.name,
              src: generateImageObject(
                {
                  url: softwareItem.image,
                  altTag: softwareItem.name
                },
                {
                  quality: 100,
                  width: 400,
                  keepAspectRatio: false
                }
              )?.content?.src
            }
          }
        : undefined,
      title: softwareItem.name ?? '',
      subtitle: softwareItem.softwareVersion
        ? `${
            $textDictionary.value['softwareCenter.downloadCard.versionLabel'] ??
            'Version'
          } ${softwareItem.softwareVersion} (${softwareItem.dateYear})`
        : `(${softwareItem.dateYear})`,
      platformsLabel:
        softwareItem.supportedOs && softwareItem.supportedOs?.length
          ? $textDictionary.value[
              'softwareCenter.downloadCard.platformsLabel'
            ] ?? 'Available for:'
          : undefined,
      platforms: findPlatforms(softwareItem.groupAssets).map((os) => ({
        iconName: os === 'macOs' ? 'mac' : os,
        text: os
      })),
      link: {
        uri: buildUrlString(
          locale.value,
          `${softwareCenterStartSlug.value}/${softwareItem.id}`,
          searchParams.toString()
        )
      },
      isModal: true
    })
  );

  const loadingData = [
    {
      id: '1',
      header: {
        type: 'image',
        content: {
          src: 'loading',
          srcset: 'loading',
          alt: 'loading'
        }
      },
      title: 'loading',
      subtitle: 'loading',
      platformsLabel: 'loading',
      platforms: undefined,
      descriptionLabel: undefined,
      description: undefined,
      link: undefined
    },
    {
      id: '2',
      header: {
        type: 'image',
        content: {
          src: 'loading',
          srcset: 'loading',
          alt: 'loading'
        }
      },
      title: 'loading',
      subtitle: 'loading',
      platformsLabel: 'loading',
      platforms: undefined,
      descriptionLabel: undefined,
      description: undefined,
      link: undefined
    },
    {
      id: '3',
      header: {
        type: 'image',
        content: {
          src: 'loading',
          srcset: 'loading',
          alt: 'loading'
        }
      },
      title: 'loading',
      subtitle: 'loading',
      platformsLabel: 'loading',
      platforms: undefined,
      descriptionLabel: undefined,
      description: undefined,
      link: undefined
    },
    {
      id: '4',
      header: {
        type: 'image',
        content: {
          src: 'loading',
          srcset: 'loading',
          alt: 'loading'
        }
      },
      title: 'loading',
      subtitle: 'loading',
      platformsLabel: 'loading',
      platforms: undefined,
      descriptionLabel: undefined,
      description: undefined,
      link: undefined
    },
    {
      id: '5',
      header: {
        type: 'image',
        content: {
          src: 'loading',
          srcset: 'loading',
          alt: 'loading'
        }
      },
      title: 'loading',
      subtitle: 'loading',
      platformsLabel: 'loading',
      platforms: undefined,
      descriptionLabel: undefined,
      description: undefined,
      link: undefined
    },
    {
      id: '6',
      header: {
        type: 'image',
        content: {
          src: 'loading',
          srcset: 'loading',
          alt: 'loading'
        }
      },
      title: 'loading',
      subtitle: 'loading',
      platformsLabel: 'loading',
      platforms: undefined,
      descriptionLabel: undefined,
      description: undefined,
      link: undefined
    }
  ];
  return results ?? (isLoadingWithMinTime.value ? loadingData : []);
});
</script>
